export function downloadFile(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Download a binary payload (file content, not a URL) by wrapping it in a short-lived
 *  object URL. Used when the backend returns the file bytes directly (e.g. catalog exports). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  downloadFile(url, filename);
  URL.revokeObjectURL(url);
}

/**
 * The download name a `Content-Disposition` header carries, or `fallback` when it carries none.
 *
 * The header is the **only** source for a file the server names itself — the admin invoice
 * download (ADO 117767/120854), whose name is slugged server-side from the order's customer.
 * Composing one client-side instead is what made two exports taken on the same day collide as
 * `… (1)`, `… (2)`, which is why the API sets `exposedHeaders: ['Content-Disposition']`.
 *
 * `filename*` (RFC 5987) wins over `filename` where both are present: it is the encoding that
 * survives non-ASCII, and a customer's name can carry it. Only the basename is returned — the
 * value reaches `a.download`, where a path separator is how a download escapes its directory.
 */
export function filenameFromContentDisposition(
  header: string | null | undefined,
  fallback: string,
): string {
  if (!header) return fallback;

  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);

  let raw = '';
  if (extended) {
    try {
      raw = decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-sequence is not worth failing a download over; fall through to
      // `filename`, then to the caller's fallback.
      raw = '';
    }
  }
  if (!raw && plain) raw = (plain[1] ?? plain[2] ?? '').trim();

  const basename = raw.split(/[\\/]/).pop()?.trim() ?? '';
  return basename || fallback;
}

export { exportFallbackName } from './fallback-name';
